import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  sliceKey,
  reducer,
  dealerDailyCdpSyncSettingsActions,
} from './slice';
import { watchDealerDailyCdpSyncSettingsSaga } from './saga';
import { selectDealerDailyCdpSyncSettings } from './selectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { initializeDealerDailyCdpSyncSettings } from './data/dealerDailyCdpSyncSettingsDataService';
import {
  AccordionComponent,
  AccordionSummaryComponent,
  AccordionDetailsComponent,
} from 'app/components/StyledComponents';
import { ConfigureInitializeState } from 'app/components/ReusableUIComponents/ConfigureInitializeState';
import { DailyCdpSyncSettingsSkeleton } from './components/DailyCdpSyncSettingsSkeleton';
import {
  DEFAULT_DAILY_CDP_SYNC_PROPERTIES,
  DAILY_CDP_SYNC_FLAG_FIELDS,
  SETTINGS_TYPE,
} from './constants';

import {
  Typography,
  Box,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type DailyCdpSyncProperties = typeof DEFAULT_DAILY_CDP_SYNC_PROPERTIES;

type DealerDailyCdpSyncSettingsComponentProps = {
  defaultExpanded?: boolean;
};

export const DealerDailyCdpSyncSettingsComponent = ({
  defaultExpanded = false,
}: DealerDailyCdpSyncSettingsComponentProps) => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({
    key: sliceKey,
    saga: watchDealerDailyCdpSyncSettingsSaga,
  });

  const dispatch = useDispatch();
  const selectedDealerId = useSelector(selectDealerSelected)?.dealer || '';
  const settingsState = useSelector(selectDealerDailyCdpSyncSettings);
  const settingsBody = settingsState.data?.body || [];
  const loading = settingsState.loading;

  const [properties, setProperties] = useState<DailyCdpSyncProperties>({
    ...DEFAULT_DAILY_CDP_SYNC_PROPERTIES,
  });
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(false);

  const hasError = (() => {
    if (!settingsState.data) return false;
    const statusText = settingsState.data?.statusText;
    const statusCode =
      settingsState.data?.statusCode ??
      settingsState.data?.myAutoIQ_status_code ??
      settingsState.data?.errorCode;

    return (
      (statusText && statusText !== 'success') ||
      (typeof statusCode === 'number' && statusCode >= 400)
    );
  })();

  useEffect(() => {
    resetLocalState();
    if (!selectedDealerId) return;
    dispatch(
      dealerDailyCdpSyncSettingsActions.requestDealerDailyCdpSyncSettings({
        dealershipId: selectedDealerId,
      }),
    );
  }, [selectedDealerId, dispatch]);

  useEffect(() => {
    loadSettingsFromStore();
  }, [settingsBody]);

  const resetLocalState = () => {
    setProperties({ ...DEFAULT_DAILY_CDP_SYNC_PROPERTIES });
    setSettingsId(null);
  };

  const hydrateProperties = (
    rowProperties: Record<string, unknown> | undefined,
  ): DailyCdpSyncProperties => {
    const next = { ...DEFAULT_DAILY_CDP_SYNC_PROPERTIES };
    if (!rowProperties) return next;
    for (const { key } of DAILY_CDP_SYNC_FLAG_FIELDS) {
      if (rowProperties[key] !== undefined) {
        next[key] = rowProperties[key] === true;
      }
    }
    return next;
  };

  const loadSettingsFromStore = () => {
    try {
      if (!settingsBody.length) {
        resetLocalState();
        return;
      }

      const block = settingsBody.find(
        (settings: { _id?: string }) => settings._id === SETTINGS_TYPE,
      );
      const row = block?.settings?.[0];

      if (row?._id && row.properties) {
        setSettingsId(row._id);
        setProperties(hydrateProperties(row.properties));
      } else {
        setSettingsId(null);
        setProperties({ ...DEFAULT_DAILY_CDP_SYNC_PROPERTIES });
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleSave = () => {
    if (!selectedDealerId || !settingsId || loading) return;

    const data = {
      type: SETTINGS_TYPE,
      name: 'Daily CDP Sync Settings',
      properties: DAILY_CDP_SYNC_FLAG_FIELDS.reduce((acc, { key }) => {
        acc[key] = properties[key] === true;
        return acc;
      }, {} as DailyCdpSyncProperties),
    };

    dispatch(
      dealerDailyCdpSyncSettingsActions.requestUpdateDealerDailyCdpSyncSettings(
        {
          dealershipId: selectedDealerId,
          data,
          settingsId,
        },
      ),
    );
  };

  const handleInitialize = async () => {
    if (!selectedDealerId || initializing) return;
    setInitializing(true);
    try {
      const response = await initializeDealerDailyCdpSyncSettings({
        dealerId: selectedDealerId,
      });
      const statusText = response?.statusText;
      if (statusText === 'success') {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'success',
            message: 'Dealer daily CDP sync settings initialized successfully',
          }),
        );
        dispatch(
          dealerDailyCdpSyncSettingsActions.requestDealerDailyCdpSyncSettings({
            dealershipId: selectedDealerId,
          }),
        );
      } else {
        throw new Error(response?.message || 'Failed to initialize settings');
      }
    } catch (error: any) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message:
            error?.message ||
            'Failed to initialize dealer daily CDP sync settings',
        }),
      );
    } finally {
      setInitializing(false);
    }
  };

  const handleError = (error: unknown) => {
    console.log(error);
    dispatch(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: 'Oops! something went wrong',
      }),
    );
  };

  const noValidData = !settingsBody.length || !settingsId;
  const showLoadingState =
    Boolean(selectedDealerId) && (loading || initializing);
  const showInitializeButton =
    !showLoadingState && selectedDealerId && (hasError || noValidData);

  return (
    <AccordionComponent
      defaultExpanded={defaultExpanded}
      elevation={0}
      data-guidance-tour="configure-daily-cdp-sync-page"
      sx={{
        '&::before': {
          display: 'none',
        },
        padding: '16px',
      }}
    >
      <AccordionSummaryComponent
        expandIcon={<ExpandMoreIcon />}
        aria-controls="dealer-daily-cdp-sync-settings-content"
        id="dealer-daily-cdp-sync-settings-header"
      >
        <Typography variant="h6" gutterBottom component="div">
          Daily CDP Sync
        </Typography>
      </AccordionSummaryComponent>
      <AccordionDetailsComponent>
        {showLoadingState ? (
          <DailyCdpSyncSettingsSkeleton />
        ) : showInitializeButton ? (
          <ConfigureInitializeState
            warningMessage={
              hasError && settingsState.data?.message
                ? settingsState.data.message
                : null
            }
            errorMessage={settingsState.error ?? null}
            message="No daily CDP sync config found. Click Initialize to set up."
            onInitialize={handleInitialize}
            initializing={initializing}
            buttonLabel="Initialize Daily CDP Sync Settings"
          />
        ) : (
          <Box>
            {DAILY_CDP_SYNC_FLAG_FIELDS.map(({ key, label }) => (
              <FormControlLabel
                key={key}
                sx={{ display: 'flex' }}
                control={
                  <Switch
                    checked={properties[key] === true}
                    onChange={() =>
                      setProperties(prev => ({
                        ...prev,
                        [key]: !prev[key],
                      }))
                    }
                    inputProps={{ 'aria-label': label }}
                  />
                }
                label={label}
              />
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || !settingsId}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
      </AccordionDetailsComponent>
    </AccordionComponent>
  );
};
